import React, { useState } from 'react';
import './index.css';
import Input from 'common/components/Input';
import SelectEl from 'common/components/Select';
import styled from '@emotion/styled';
import TextArea from 'common/components/TextArea';
import { companySizeData, industryData, workspaceTypeData } from 'common/data/registrationDropdowns';
import Button from 'common/components/Button';
import DefaultImage from 'common/images/default-avatar.png';
import { useAuth } from 'common/context/AuthContext';
import { SelectChangeEvent } from '@mui/material';

const StyledInputLine = styled.div`
    display:flex;
    gap: 16px;
    align-items: center;
    justify-content: space-between;
`;

const StyledImageLink = styled.p`
    color: ${(props) => props.theme.colors.primaryColor};
    cursor: pointer;
    &:hover {
        text-decoration: underline;
    }
`;

const StyledImageWrapper = styled.div`
    height: 100px;
    width: 100px;
    border-radius: 16px;
    overflow: hidden;
    position: relative;
    display:flex;
    justify-content: center;
    align-items: center;
    border: 1px solid ${(props) => props.theme.colors.gray};
`;

const categoryData = [
  {
    value: 0,
    label: 'Категорія профілю',
  },
  {
    value: 1,
    label: 'Категорія профілю 1',
  },
  {
    value: 2,
    label: 'Категорія профілю 2',
  },
  {
    value: 3,
    label: 'Категорія профілю 3',
  },
  {
    value: 4,
    label: 'Категорія профілю 4',
  },
];

const InputLine = ({ children, label }: { children?: React.ReactNode, label: string }) => {
  return (
            <StyledInputLine>
                <p className='new-p settings-account-line-title'>{label}</p>
                {children}
            </StyledInputLine>
  );
};

type AccountSettingsFormProps = {
  name: string;
  workspaceUrl: string;
  category: number;
  workspaceType: number;
  industry: number;
  specialization: number;
  companySize: number;
  website?: string;
  about?: string;
  email: string;
  emailReserve?: string;
  phone: string;
  phoneReserve?: string;
  country: string;
  city?: string;
  street?: string;
  house?: string;
  appartment?: string;
  foundationDate: string;
  edrpou: string;
  socialNetworks?: string;
};

const AccountSettingsCleanForm: AccountSettingsFormProps = {
  name: '',
  workspaceUrl: '',
  category: 0,
  workspaceType: 0,
  industry: 0,
  specialization: 0,
  companySize: 0,
  website: '',
  about: '',
  email: '',
  emailReserve: '',
  phone: '',
  phoneReserve: '',
  country: '',
  city: '',
  street: '',
  house: '',
  appartment: '',
  foundationDate: '',
  edrpou: '',
  socialNetworks: '',
};

type AccountSettingsFormErrorsProps = {
  name: boolean | string;
  workspaceUrl: boolean | string;
  category: boolean;
  workspaceType: boolean;
  industry: boolean;
  specialization: boolean;
  companySize: boolean;
  website: boolean | string;
  about: boolean | string;
  email: boolean | string;
  emailReserve: boolean | string;
  phone: boolean | string;
  phoneReserve: boolean | string;
  country: boolean | string;
  city: boolean | string;
  street: boolean | string;
  house: boolean | string;
  appartment: boolean | string;
  foundationDate: boolean | string;
  edrpou: boolean | string;
  socialNetworks: boolean | string;
};

const AccountSettingsCleanFormErrors: AccountSettingsFormErrorsProps = {
  name: false,
  workspaceUrl: false,
  category: false,
  workspaceType: false,
  industry: false,
  specialization: false,
  companySize: false,
  website: false,
  about: false,
  email: false,
  emailReserve: false,
  phone: false,
  phoneReserve: false,
  country: false,
  city: false,
  street: false,
  house: false,
  appartment: false,
  foundationDate: false,
  edrpou: false,
  socialNetworks: false,
};

export default function AccountSettingsPage() {
  const { companyId } = useAuth(); 
  const [form, setForm] = useState<AccountSettingsFormProps>(AccountSettingsCleanForm);
  const [formErrors, setFormErrors] = useState<AccountSettingsFormErrorsProps>(AccountSettingsCleanFormErrors);

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
    // check for errors, remove if value is not empty now
    if (formErrors[e.target.name]) {
      if (e.target.value !== '') {
        setFormErrors((prev) => ({ ...prev, [e.target.name]: false }));
      }
    }
    setForm({ ...form, [e.target.name]: e.target.value });
  };
  const handleSelectChange = (e: SelectChangeEvent) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  return (
        <form className='settings-account-form'>
            <h3 className='new-h3 settings-account-title' >Акаунт</h3>
            <div className='settings-account-image-section'>
                <StyledImageWrapper>
                    <img src={DefaultImage} alt='profile' className='settings-account-image' />
                </StyledImageWrapper>
                <div className='settings-account-image-text-wrapper'>
                    <p className='new-p'>Одеська територіальна громада</p>
                    <p className='new-p'>omr</p>
                    <StyledImageLink className='new-p settings-account-image-link'>змінити світлину</StyledImageLink>
                </div>
            </div>
            <InputLine label={`id ${companyId}`} />
            <InputLine label='Назва акаунту*'>
                <Input
                    value={form.name}
                    className='settings-account-input'
                    placeholder='Назва акаунту'
                    name='name'
                    error={formErrors.name}
                    onChange={handleValueChange}
                />
            </InputLine>
            <InputLine label='URL-адреса робочого простіру*'>
                <Input
                    value={form.workspaceUrl}
                    className='settings-account-input'
                    placeholder='URL-адреса робочого простіру'
                    name='workspaceUrl'
                    error={formErrors.workspaceUrl}
                    onChange={handleValueChange}
                />
            </InputLine>
            <InputLine label='Категорія профілю*'>
                <div className='settings-account-input'>
                    <SelectEl
                        onChange={handleSelectChange}
                        value={form.category}
                        data={categoryData}
                        name='category'
                        error={formErrors.category}
                    />
                </div>
            </InputLine>
            <InputLine label='Тип робочого простору*'>
                <div className='settings-account-input'>
                    <SelectEl
                        onChange={handleSelectChange}
                        value={form.workspaceType}
                        data={workspaceTypeData}
                        placeholder='Оберіть тип робочого простору'
                        name='workspaceType'
                        error={formErrors.workspaceType}
                    />
                </div>
            </InputLine>
            <InputLine label='Галузь*'>
                <div className='settings-account-input'>
                    <SelectEl
                        onChange={handleSelectChange}
                        value={form.industry}
                        data={industryData}
                        placeholder='Оберіть галузь'
                        name='industry'
                        error={formErrors.industry}
                    />
                </div>
            </InputLine>
            <InputLine label='Спеціалізація*'>
                <div className='settings-account-input'>
                    <SelectEl
                        onChange={handleSelectChange}
                        value={form.specialization}
                        disabled={form.industry === 0 ? true : false}
                        data={industryData[form.industry - 1]?.specialization || []}
                        placeholder='Оберіть спеціалізацію'
                        name='specialization'
                        error={formErrors.specialization}
                    />
                </div>
            </InputLine>
            <InputLine label='Розмір компанії*'>
                <div className='settings-account-input'>
                    <SelectEl
                        onChange={handleSelectChange}
                        value={form.companySize}
                        data={companySizeData}
                        placeholder='Оберіть розмір компанії'
                        name='companySize'
                        error={formErrors.companySize}
                    />
                </div>
            </InputLine>
            <InputLine label='Веб-сайт'>
                <Input
                    value={form.website}
                    className='settings-account-input'
                    placeholder='Веб-сайт'
                    name='website'
                    error={formErrors.website}
                    onChange={handleValueChange}
                />
            </InputLine>
            <InputLine label='Про нас'>
                <TextArea
                    placeholder='Опишіть важливу інформацію'
                    className='add-user-input'
                    name='about'
                    value={form.about}
                    props={{ autoComplete: 'off', maxLength: 150 }}
                    error={formErrors.about}
                    onChange={handleValueChange}
                />
            </InputLine>
            <span className='small-text add-user-textarea-count'>{form.about.length} / 150</span>
            <InputLine label='E-пошта*'>
                <Input
                    value={form.email}
                    className='settings-account-input' 
                    placeholder='E-пошта'
                    name='email'
                    error={formErrors.email}
                    onChange={handleValueChange}
                />
            </InputLine>
            <InputLine label='E-пошта (резервна)'>
                <Input
                    value={form.emailReserve}
                    className='settings-account-input' 
                    placeholder='E-пошта (резервна)'
                    name='emailReserve'
                    error={formErrors.emailReserve}
                    onChange={handleValueChange}
                />
            </InputLine>
            <InputLine label='Телефон*'>
                <Input
                    value={form.phone}
                    className='settings-account-input' 
                    placeholder='Телефон'
                    name='phone'
                    error={formErrors.phone}
                    onChange={handleValueChange}
                />
            </InputLine>
            <InputLine label='Телефон (резервний)'>
                <Input
                    value={form.phoneReserve}
                    className='settings-account-input' 
                    placeholder='Телефон (резервний)'
                    name='phoneReserve'
                    error={formErrors.phoneReserve}
                    onChange={handleValueChange}
                />
            </InputLine>
            <InputLine label='Країна'>
                <Input
                    value={form.country}
                    className='settings-account-input' 
                    placeholder='Країна'
                    name='country'
                    error={formErrors.country}
                    onChange={handleValueChange}
                />
            </InputLine>
            <InputLine label='Місто'>
                <Input
                    value={form.city}
                    className='settings-account-input' 
                    placeholder='Місто'
                    name='city'
                    error={formErrors.city}
                    onChange={handleValueChange}
                />
            </InputLine>
            <InputLine label='Вулиця'>
                <Input
                    value={form.street}
                    className='settings-account-input' 
                    placeholder='Вулиця'
                    name='street'
                    error={formErrors.street}
                    onChange={handleValueChange}
                />
            </InputLine>
            <InputLine label='Будинок'>
                <Input
                    value={form.house}
                    className='settings-account-input' 
                    placeholder='Будинок'
                    name='house'
                    error={formErrors.house}
                    onChange={handleValueChange}
                />
            </InputLine>
            <InputLine label='Квартира'>
                <Input
                    value={form.appartment}
                    className='settings-account-input' 
                    placeholder='Квартира'
                    name='appartment'
                    error={formErrors.appartment}
                    onChange={handleValueChange}
                />
            </InputLine>
            <InputLine label='Дата заснування*'>
                <Input
                    placeholder='Дата заснування'
                    className='add-user-input'
                    name='foundationDate'
                    value={form.foundationDate}
                    props={{ autoComplete: 'off', type: 'date' }}
                    error={formErrors.foundationDate}
                    onChange={handleValueChange}
                />
            </InputLine>
            <InputLine label='ЄДРПОУ*'>
                <Input
                    value={form.edrpou}
                    className='settings-account-input' 
                    placeholder='ЄДРПОУ'
                    name='edrpou'
                    error={formErrors.edrpou}
                    onChange={handleValueChange}
                />
            </InputLine>
            <InputLine label='Додати соц. мережі'>
                <Input
                    value={form.socialNetworks}
                    className='settings-account-input' 
                    placeholder='Додати соц. мережі'
                    name='socialNetworks'
                    error={formErrors.socialNetworks}
                    onChange={handleValueChange}
                />
            </InputLine>
            <div className='settings-account-buttons'>
                <Button type='button' label='Скасувати' />
                <Button type='submit' label='Зберегти' primary />
            </div>

        </form>
  );
}